import { useMutation, useQueryClient } from '@tanstack/react-query';
import TodoService, {
  IUpdateTodoParams,
} from '@clickersphere/core/services/TodoService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { TodoQueryData, TODOS_QUERY_KEY } from './useTodos';

export function useUpdateTodo() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const { isPending: updateTodoIsPending, mutateAsync: updateTodoAsync } =
    useMutation({
      mutationFn: async ({
        id,
        data,
      }: {
        id: string;
        data: IUpdateTodoParams;
      }) => TodoService.update(id, { ...data }),

      onMutate: async ({ id, data }) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(TODOS_QUERY_KEY),
          exact: false,
        });

        const previousData = queryClient.getQueriesData<TodoQueryData[]>({
          queryKey: buildQueryKey(TODOS_QUERY_KEY),
          exact: false,
        });

        queryClient.setQueriesData<TodoQueryData[]>(
          { queryKey: buildQueryKey(TODOS_QUERY_KEY), exact: false },
          (old) =>
            old?.map((todo) => {
              if (todo.id !== id) return todo;
              const next: TodoQueryData = {
                ...todo,
                ...data,
                leadId:
                  data.leadId === null
                    ? undefined
                    : (data.leadId ?? todo.leadId),
                eventId:
                  data.eventId === null
                    ? undefined
                    : (data.eventId ?? todo.eventId),
                queryStatus: 'pending',
              };
              return next;
            }),
        );

        return { previousData };
      },

      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey(TODOS_QUERY_KEY),
          exact: false,
        });
      },

      onError: async (_error, _input, context) => {
        if (context?.previousData) {
          context.previousData.forEach(([key, value]) => {
            queryClient.setQueryData(key, value);
          });
        }
      },
    });

  return {
    updateTodoAsync,
    updateTodoIsPending,
  };
}
